import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Printer, Clock, CheckCircle, AlertCircle, ExternalLink, Scale, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { signaturesAPI, poaAPI, referralAPI } from '../utils/api';

const SignedDocuments = ({ isAdmin = false }) => {
  const [documents, setDocuments] = useState([]);
  const [poaDocuments, setPOADocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [filter]);

  const loadDocuments = async () => {
    try {
      const status = filter === 'all' ? null : filter;
      
      // Load both regular signed documents and POA documents
      const [signaturesRes, poaRes] = await Promise.all([
        signaturesAPI.getSignedDocuments(status).catch(() => ({ data: [] })),
        poaAPI.getAllPOA(status).catch(() => ({ data: [] }))
      ]);
      
      setDocuments(signaturesRes.data || []);
      setPOADocuments(poaRes.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, title) => {
    try {
      const response = await signaturesAPI.getDocumentPdf(documentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handlePrint = async (documentId) => {
    try {
      const response = await signaturesAPI.getDocumentPdf(documentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      toast.error('Failed to print document');
    }
  };

  const handleView = async (documentId, title) => {
    try {
      setPreviewLoading(true);
      setPreviewTitle(title);
      const response = await signaturesAPI.getDocumentPdf(documentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to view document');
    } finally {
      setPreviewLoading(false);
    }
  };

  // POA specific handlers
  const handlePOAView = async (poaId, title = 'Limited Power of Attorney') => {
    try {
      setPreviewLoading(true);
      setPreviewTitle(title);
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to view POA document');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle('');
  };

  const handlePOADownload = async (poaId, caseName) => {
    try {
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Limited_POA_${caseName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('POA downloaded successfully');
    } catch (error) {
      toast.error('Failed to download POA');
    }
  };

  const handlePOAPrint = async (poaId, title = 'Document') => {
    try {
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      printFrame.src = url;
      
      document.body.appendChild(printFrame);
      
      printFrame.onload = () => {
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (e) {
            // Fallback: open in new tab with print dialog
            const newTab = window.open(url, '_blank');
            if (newTab) {
              toast.info('Please use your browser\'s print function (Ctrl+P)');
            } else {
              // If popup blocked, show preview modal instead
              setPreviewUrl(url);
              setPreviewTitle(title);
              toast.info('Popup blocked. Use the Download button to save and print.');
            }
          }
          // Clean up
          setTimeout(() => {
            document.body.removeChild(printFrame);
            window.URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      };
      
      toast.success('Preparing document for printing...');
    } catch (error) {
      toast.error('Failed to print POA');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_client: { 
        label: 'Awaiting Client Signature', 
        class: 'bg-amber-100 text-amber-700',
        icon: Clock
      },
      pending_admin: { 
        label: 'Awaiting Admin Review', 
        class: 'bg-blue-100 text-blue-700',
        icon: AlertCircle
      },
      completed: { 
        label: 'Completed', 
        class: 'bg-green-100 text-green-700',
        icon: CheckCircle
      }
    };
    const badge = badges[status] || badges.pending_client;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-600">
        Loading signed documents...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Signed Documents</h2>
          <p className="text-sm text-slate-600">View and download all signed agreements and contracts</p>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'pending_admin', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f === 'all' ? 'All' : f === 'pending_admin' ? 'Pending Review' : 'Completed'}
            </button>
          ))}
        </div>
      </div>

      {/* POA Documents Section */}
      {poaDocuments.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Scale className="text-purple-600" size={20} />
            Limited Power of Attorney Documents
          </h3>
          <div className="space-y-4">
            {poaDocuments.map((poa) => (
              <div 
                key={poa.id} 
                className="border-2 border-purple-200 bg-purple-50 rounded-lg p-4 hover:border-purple-400 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                      <Scale className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Limited Power of Attorney</h3>
                      <p className="text-sm text-slate-600">
                        Case: {poa.case_name} • {poa.form_data?.merchant_legal_name}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>Created: {format(new Date(poa.created_at), 'MMM dd, yyyy')}</span>
                        {poa.completed_at && (
                          <span className="text-green-600 font-medium">
                            Completed: {format(new Date(poa.completed_at), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                      {poa.status === 'completed' && (
                        <div className="mt-2 text-xs text-slate-600">
                          <span className="font-medium">Signers:</span> {poa.client_signature?.signer_name} (Client) • {poa.admin_signature?.signer_name} (Agent)
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {getStatusBadge(poa.status)}
                    
                    <div className="flex gap-1">
                      <button
                        onClick={() => handlePOAView(poa.id, `Limited POA - ${poa.case_name}`)}
                        className="p-2 text-slate-600 hover:bg-purple-100 rounded-lg transition-colors"
                        title="View PDF"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handlePOADownload(poa.id, poa.case_name)}
                        className="p-2 text-slate-600 hover:bg-purple-100 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => handlePOAPrint(poa.id)}
                        className="p-2 text-slate-600 hover:bg-purple-100 rounded-lg transition-colors"
                        title="Print"
                      >
                        <Printer size={18} />
                      </button>
                      {poa.dropbox_url && (
                        <a
                          href={poa.dropbox_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View in Dropbox"
                        >
                          <ExternalLink size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Signed Documents Section */}
      {documents.length === 0 && poaDocuments.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <FileText className="mx-auto text-slate-400 mb-4" size={48} />
          <p className="text-slate-600">No signed documents found</p>
        </div>
      ) : documents.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="text-purple-600" size={20} />
            Other Signed Documents
          </h3>
          {documents.map((doc) => (
            <div 
              key={doc.id} 
              className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{doc.title}</h3>
                    <p className="text-sm text-slate-600 capitalize">
                      {doc.document_type.replace(/_/g, ' ')}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>Created: {format(new Date(doc.created_at), 'MMM dd, yyyy')}</span>
                      {doc.completed_at && (
                        <span>Completed: {format(new Date(doc.completed_at), 'MMM dd, yyyy')}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(doc.status)}
                  
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleView(doc.id, doc.title)}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View PDF"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleDownload(doc.id, doc.title)}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Download PDF"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => handlePrint(doc.id)}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Print"
                    >
                      <Printer size={18} />
                    </button>
                    {doc.dropbox_url && (
                      <a
                        href={doc.dropbox_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View in Dropbox"
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Signatures Info */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-4">
                  {/* Client Signatures */}
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">CLIENT SIGNATURES</h4>
                    {doc.client_signatures?.length > 0 ? (
                      <div className="space-y-2">
                        {doc.client_signatures.map((sig, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium text-slate-900">{sig.signer_name}</span>
                            <span className="text-slate-500"> ({sig.signer_title})</span>
                            <div className="text-xs text-slate-400">
                              {format(new Date(sig.signed_at), 'MMM dd, yyyy at h:mm a')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Pending...</span>
                    )}
                  </div>
                  
                  {/* Admin Signatures */}
                  <div>
                    <h4 className="text-xs font-medium text-slate-500 mb-2">RESERVERELEASE.AI SIGNATURES</h4>
                    {doc.admin_signatures?.length > 0 ? (
                      <div className="space-y-2">
                        {doc.admin_signatures.map((sig, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium text-slate-900">{sig.signer_name}</span>
                            <span className="text-slate-500"> ({sig.signer_title})</span>
                            <div className="text-xs text-slate-400">
                              {format(new Date(sig.signed_at), 'MMM dd, yyyy at h:mm a')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Pending admin review...</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">{previewTitle}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  Download
                </a>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                >
                  <ExternalLink size={16} />
                  Open in New Tab
                </a>
                <button
                  onClick={closePreview}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* PDF Content */}
            <div className="flex-1 p-4 overflow-hidden">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading document...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col">
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full flex-1 border border-slate-200 rounded-lg"
                  >
                    <div className="flex flex-col items-center justify-center h-full bg-slate-50 rounded-lg p-8">
                      <FileText className="text-slate-400 mb-4" size={64} />
                      <p className="text-slate-600 mb-4 text-center">
                        Your browser cannot display this PDF inline.
                      </p>
                      <div className="flex gap-3">
                        <a
                          href={previewUrl}
                          download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <Download size={18} />
                          Download PDF
                        </a>
                        <a
                          href={previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                        >
                          <ExternalLink size={18} />
                          Open in New Tab
                        </a>
                      </div>
                    </div>
                  </object>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignedDocuments;
